import { useState, useEffect } from "react";
import axios from "axios";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { ArrowLeft, Search, RefreshCw, FileText, Clock, User, DollarSign } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { formatAdminError } from "../lib/adminError";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

axios.defaults.withCredentials = true;

const EVENT_TYPE_LABELS = {
  session_count_override: "Session-Count Override",
  credit_grant: "Credit Granted",
  credit_use: "Credit Consumed",
  refund: "Refund",
  waiver: "Waiver",
};

const EVENT_TYPE_COLORS = {
  session_count_override: "text-amber-400 bg-amber-500/10 border-amber-500/30",
  credit_grant: "text-emerald-400 bg-emerald-500/10 border-emerald-500/30",
  credit_use: "text-blue-400 bg-blue-500/10 border-blue-500/30",
  refund: "text-rose-400 bg-rose-500/10 border-rose-500/30",
  waiver: "text-slate-400 bg-slate-500/10 border-slate-500/30",
};

export default function AdminBillingAdjustments() {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    parent_email: "",
    player_id: "",
    term_id: "",
    event_type: "",
    start_date: "",
    end_date: "",
  });

  const fetchEvents = async () => {
    setLoading(true);
    try {
      const params = {};
      Object.entries(filters).forEach(([k, v]) => {
        if (v && v.trim()) params[k] = v.trim();
      });
      const resp = await axios.get(`${API}/academy/admin/billing-events`, { params });
      setEvents(resp.data.events || []);
      setSummary(resp.data.summary || null);
    } catch (err) {
      const { message, opts } = formatAdminError(err, "Failed to load billing events");
      toast.error(message, opts);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEvents();
    // eslint-disable-next-line
  }, []);

  const updateFilter = (key) => (e) => setFilters((f) => ({ ...f, [key]: e.target.value }));

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 text-white p-6">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center gap-3 mb-6">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate("/admin")}
            data-testid="billing-adjustments-back"
            className="border-slate-700 text-slate-300 hover:bg-slate-800"
          >
            <ArrowLeft className="w-4 h-4 mr-2" /> Admin
          </Button>
          <div>
            <h1 className="text-2xl font-bold">Billing Adjustments &amp; Credits</h1>
            <p className="text-sm text-slate-400 mt-1">
              Unified audit trail of every time a parent was billed or credited
              outside the standard <span className="font-mono">term_weeks × session_price</span> formula.
              Covers session-count overrides, credit grants/uses, refunds and waivers.
            </p>
          </div>
        </div>

        {/* Summary cards */}
        {summary && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            <Card className="p-4 bg-slate-800/60 border-slate-700">
              <p className="text-xs uppercase tracking-wider text-slate-400">Events</p>
              <p className="text-2xl font-bold text-white mt-1" data-testid="summary-total-events">
                {summary.total_events}
              </p>
            </Card>
            <Card className="p-4 bg-slate-800/60 border-slate-700">
              <p className="text-xs uppercase tracking-wider text-slate-400">Net Session Δ</p>
              <p className="text-2xl font-bold text-amber-400 mt-1">
                {summary.total_sessions_delta > 0 ? "+" : ""}
                {summary.total_sessions_delta}
              </p>
            </Card>
            <Card className="p-4 bg-slate-800/60 border-slate-700">
              <p className="text-xs uppercase tracking-wider text-slate-400">Net $ Δ</p>
              <p className="text-2xl font-bold text-emerald-400 mt-1">
                ${(summary.total_dollar_delta || 0).toFixed(2)}
              </p>
            </Card>
            <Card className="p-4 bg-slate-800/60 border-slate-700">
              <p className="text-xs uppercase tracking-wider text-slate-400">Breakdown</p>
              <div className="text-xs text-slate-300 mt-1 space-y-0.5">
                {Object.entries(summary.by_event_type || {}).map(([k, v]) => (
                  <div key={k}>
                    <span className="text-slate-400">{EVENT_TYPE_LABELS[k] || k}:</span>{" "}
                    <span className="font-semibold">{v}</span>
                  </div>
                ))}
              </div>
            </Card>
          </div>
        )}

        {/* Filters */}
        <Card className="p-4 mb-6 bg-slate-800/40 border-slate-700">
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-3">
            <div>
              <Label htmlFor="f-email" className="text-xs text-slate-400">Parent email</Label>
              <Input
                id="f-email"
                data-testid="filter-parent-email"
                value={filters.parent_email}
                onChange={updateFilter("parent_email")}
                placeholder="dheejs@gmail.com"
                className="mt-1 bg-slate-900 border-slate-700 text-white"
              />
            </div>
            <div>
              <Label htmlFor="f-player" className="text-xs text-slate-400">Player ID</Label>
              <Input
                id="f-player"
                data-testid="filter-player-id"
                value={filters.player_id}
                onChange={updateFilter("player_id")}
                placeholder="uuid…"
                className="mt-1 bg-slate-900 border-slate-700 text-white"
              />
            </div>
            <div>
              <Label htmlFor="f-term" className="text-xs text-slate-400">Term ID</Label>
              <Input
                id="f-term"
                data-testid="filter-term-id"
                value={filters.term_id}
                onChange={updateFilter("term_id")}
                placeholder="term uuid"
                className="mt-1 bg-slate-900 border-slate-700 text-white"
              />
            </div>
            <div>
              <Label htmlFor="f-type" className="text-xs text-slate-400">Event type</Label>
              <Select
                value={filters.event_type || "__all__"}
                onValueChange={(v) => setFilters((f) => ({ ...f, event_type: v === "__all__" ? "" : v }))}
              >
                <SelectTrigger id="f-type" data-testid="filter-event-type" className="mt-1 bg-slate-900 border-slate-700 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-700 text-white">
                  <SelectItem value="__all__">All types</SelectItem>
                  {Object.entries(EVENT_TYPE_LABELS).map(([k, v]) => (
                    <SelectItem key={k} value={k}>{v}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="f-start" className="text-xs text-slate-400">From date</Label>
              <Input
                id="f-start"
                data-testid="filter-start-date"
                type="date"
                value={filters.start_date}
                onChange={updateFilter("start_date")}
                className="mt-1 bg-slate-900 border-slate-700 text-white"
              />
            </div>
            <div>
              <Label htmlFor="f-end" className="text-xs text-slate-400">To date</Label>
              <Input
                id="f-end"
                data-testid="filter-end-date"
                type="date"
                value={filters.end_date}
                onChange={updateFilter("end_date")}
                className="mt-1 bg-slate-900 border-slate-700 text-white"
              />
            </div>
          </div>
          <div className="flex gap-2 mt-4">
            <Button
              onClick={fetchEvents}
              disabled={loading}
              data-testid="apply-filters-btn"
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              {loading ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Search className="w-4 h-4 mr-2" />}
              Apply filters
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                setFilters({ parent_email: "", player_id: "", term_id: "", event_type: "", start_date: "", end_date: "" });
                setTimeout(fetchEvents, 50);
              }}
              className="border-slate-600 text-slate-300"
            >
              Clear
            </Button>
          </div>
        </Card>

        {/* Events table */}
        <Card className="overflow-hidden bg-slate-800/40 border-slate-700">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-900/60 text-xs uppercase tracking-wider text-slate-400">
                <tr>
                  <th className="px-4 py-3 text-left">When</th>
                  <th className="px-4 py-3 text-left">Type</th>
                  <th className="px-4 py-3 text-left">Player</th>
                  <th className="px-4 py-3 text-left">Parent</th>
                  <th className="px-4 py-3 text-left">Term</th>
                  <th className="px-4 py-3 text-right">Session Δ</th>
                  <th className="px-4 py-3 text-right">$ Δ</th>
                  <th className="px-4 py-3 text-left">Reason</th>
                  <th className="px-4 py-3 text-left">By</th>
                </tr>
              </thead>
              <tbody data-testid="events-table-body">
                {events.length === 0 && (
                  <tr><td colSpan={9} className="px-4 py-12 text-center text-slate-500">
                    {loading ? "Loading…" : "No events match the current filters."}
                  </td></tr>
                )}
                {events.map((e) => (
                  <tr key={e.id} className="border-t border-slate-700/60 hover:bg-slate-700/20">
                    <td className="px-4 py-3 text-slate-300 whitespace-nowrap">
                      <Clock className="inline w-3 h-3 mr-1 text-slate-500" />
                      {(e.created_at || "").slice(0, 16).replace("T", " ")}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded text-xs font-medium border ${EVENT_TYPE_COLORS[e.event_type] || EVENT_TYPE_COLORS.waiver}`}>
                        {EVENT_TYPE_LABELS[e.event_type] || e.event_type}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-white">
                      <User className="inline w-3 h-3 mr-1 text-slate-500" />
                      {e.player_name || <span className="text-slate-500">—</span>}
                    </td>
                    <td className="px-4 py-3 text-slate-300">{e.parent_email || <span className="text-slate-500">—</span>}</td>
                    <td className="px-4 py-3 text-slate-300">{e.term_name || <span className="text-slate-500">—</span>}</td>
                    <td className={`px-4 py-3 text-right font-mono ${(e.sessions_delta || 0) > 0 ? "text-emerald-400" : (e.sessions_delta || 0) < 0 ? "text-rose-400" : "text-slate-500"}`}>
                      {e.sessions_delta ? (e.sessions_delta > 0 ? `+${e.sessions_delta}` : e.sessions_delta) : "—"}
                    </td>
                    <td className={`px-4 py-3 text-right font-mono ${(e.dollar_delta || 0) > 0 ? "text-emerald-400" : (e.dollar_delta || 0) < 0 ? "text-rose-400" : "text-slate-500"}`}>
                      {e.dollar_delta ? `${e.dollar_delta > 0 ? "+" : ""}$${Math.abs(e.dollar_delta).toFixed(2)}${e.dollar_delta < 0 ? " (consumed)" : ""}` : "—"}
                    </td>
                    <td className="px-4 py-3 text-slate-400 max-w-xs">
                      <div className="truncate" title={e.reason_details || ""}>
                        {e.reason_details || e.reason || <span className="text-slate-500">—</span>}
                      </div>
                      {e.invoice_reference && (
                        <div className="text-xs text-slate-500 mt-0.5 font-mono">
                          <FileText className="inline w-3 h-3 mr-1" />
                          {e.invoice_reference}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-slate-400 text-xs">
                      {e.created_by_name || <span className="text-slate-500">—</span>}
                      <div className="text-slate-600">{e.source}</div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>

        <div className="mt-6 p-4 bg-slate-800/40 border border-slate-700 rounded-lg text-xs text-slate-400 flex items-start gap-2">
          <DollarSign className="w-4 h-4 text-slate-500 mt-0.5 flex-shrink-0" />
          <div>
            <p className="font-medium text-slate-300 mb-1">Sign convention</p>
            <p>
              <span className="font-mono text-emerald-400">+</span> = owed TO parent (sessions credited, dollars credited).
              {" "}
              <span className="font-mono text-rose-400">−</span> = consumed BY parent (credit used, billed fewer sessions than planned reduces the bill but has no dollar-delta here because it's already in the invoice total).
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
